// @school-apps/core/hub — the PURE personal-event validator (CAL·1's rules, extracted at CAL·4 so
// the client can import it WITHOUT pulling the route/`http` code). One source of truth: `events.js`
// re-exports these, the server route validates with `validateEvent`, and the CAL·4 Add/Edit form
// gates its Save button with the SAME function — the client never forks the shape rules.
//
// Depends only on `isDue` from lib/dates (a pure predicate already bundled client-side). No node:http,
// no DB, no clock — safe to esbuild into the browser bundle.
import { isDue } from "../lib/dates.js";

export const EVENT_KINDS = ["activity", "work", "appointment", "personal", "other"];
const DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// present = provided AND not null (null is the caller's "clear this field" signal on update)
const has = (v) => v !== undefined && v !== null;

/**
 * Validate a candidate event's whole shape and return its canonical payload fields (no id/createdAt).
 * PURE + exported: it validates a create body directly, and update runs it over the merged record so
 * the one-off-XOR-recurring and time-window cross-field invariants hold after any partial edit.
 * @param {object} input
 * @returns {{ error: string } | { fields: object }}
 */
export function validateEvent(input) {
  const fields = {};

  if (typeof input.title !== "string" || !input.title.trim()) return { error: "title is required" };
  fields.title = input.title.trim();

  if (!EVENT_KINDS.includes(input.kind)) return { error: "kind must be one of " + EVENT_KINDS.join(", ") };
  fields.kind = input.kind;

  if (has(input.color)) {
    if (typeof input.color !== "string" || !COLOR_RE.test(input.color)) return { error: "color must be a #rrggbb hex string" };
    fields.color = input.color;
  }

  if (has(input.note)) {
    if (typeof input.note !== "string") return { error: "note must be a string" };
    if (input.note.trim()) fields.note = input.note.trim(); // empty note = absent
  }

  // one-off XOR recurring — decided by which fields are present
  const oneOff = has(input.date);
  const recurring = has(input.days) || has(input.from) || has(input.to);
  if (oneOff && recurring) return { error: "an event is either one-off (date) or recurring (days/from/to), not both" };
  if (!oneOff && !recurring) return { error: "an event needs either a date or recurring days/from/to" };

  if (oneOff) {
    if (!isDue(input.date)) return { error: "date must be a YYYY-MM-DD date" };
    fields.date = input.date;
  } else {
    if (!Array.isArray(input.days) || input.days.length === 0) return { error: "days must be a non-empty array of weekday codes" };
    if (!input.days.every((d) => DAYS.includes(d))) return { error: "days must be weekday codes (MO…SU)" };
    if (!isDue(input.from)) return { error: "from must be a YYYY-MM-DD date" };
    if (!isDue(input.to)) return { error: "to must be a YYYY-MM-DD date" };
    if (input.to < input.from) return { error: "to must be on or after from" };
    fields.days = input.days;
    fields.from = input.from;
    fields.to = input.to;
  }

  // optional time window — both absent = all-day; end required + strictly after start when present
  const hasStart = has(input.start);
  const hasEnd = has(input.end);
  if (hasStart || hasEnd) {
    if (hasStart && !hasEnd) return { error: "end is required when start is present" };
    if (hasEnd && !hasStart) return { error: "start is required when end is present" };
    if (typeof input.start !== "string" || !TIME_RE.test(input.start)) return { error: "start must be an HH:MM time" };
    if (typeof input.end !== "string" || !TIME_RE.test(input.end)) return { error: "end must be an HH:MM time" };
    if (input.end <= input.start) return { error: "end must be after start" };
    fields.start = input.start;
    fields.end = input.end;
  }

  return { fields };
}
